import { useEffect, useState, type ReactNode } from 'react';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { inputClass, cx } from './utils.js';

export type ColorFieldProps = {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
};

/** Color swatch + hex field, without raw OS-only chrome as the only control. */
export function ColorField({
  value,
  onChange,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: ColorFieldProps): ReactNode {
  const { text } = useAdminLocale();
  const [hex, setHex] = useState(value);

  useEffect(() => {
    setHex(value);
  }, [value]);

  return (
    <div className={cx('mt-1.5 flex gap-2', className)} data-ui="color-field">
      <label className="relative size-10 shrink-0 overflow-hidden rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 shadow-sm">
        <span className="absolute inset-1 rounded-md" style={{ background: value }} aria-hidden />
        <input
          type="color"
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#2563EB'}
          disabled={disabled}
          aria-label={ariaLabel ?? text('Επιλογή χρώματος', 'Choose color')}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </label>
      <input
        value={hex}
        disabled={disabled}
        aria-label={text('Κωδικός χρώματος', 'Color code')}
        onChange={(event) => {
          const next = event.target.value.toUpperCase();
          setHex(next);
          if (/^#[0-9A-F]{6}$/.test(next)) onChange(next);
        }}
        className={`${inputClass} mt-0`}
      />
    </div>
  );
}
